import React from 'react';
import { Helmet } from 'react-helmet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import ButtonCTA from '@/components/ButtonCTA';
import Breadcrumb from '@/components/Breadcrumb';
import CTASection from '@/components/CTASection';
import { CRJMC_PRICE } from '@/constants';

const FAQPage = () => {
  const faqData = {
    service: {
      title: "Questions about CRJMC.net Service",
      items: [
        {
          question: "How much does a Criminal Record and Judicial Matters Check cost at CRJMC.net?",
          answer: `The CRJMC costs <strong>CAD $${CRJMC_PRICE}</strong> at CRJMC.net. This includes:<ul><li>• <strong>Fast processing</strong> (15-minute turnaround)</li><li>• <strong>Secure online delivery</strong></li><li>• <strong>No in-person visits required</strong></li></ul>Unlike other providers, we offer <strong>transparent pricing</strong> with no hidden fees.`
        },
        {
          question: "Is there a discount for renewals?",
          answer: "Yes! When you complete your initial CRJMC with us, you automatically qualify for a <strong>20% discount</strong> on all future renewals. This offer:<ul><li>• <strong>Never expires</strong></li><li>• Applies to all future renewals</li><li>• Is automatically applied when using the same email address</li></ul>Visit our <a href='/renewal-discount' className='text-emerald-700 hover:underline'>renewal discount page</a> to learn more."
        },
        {
          question: "How do I get started with my police record check at CRJMC.net?",
          answer: "Getting your Criminal Record and Judicial Matters Check (CRJMC) at CRJMC.net is <strong>easy</strong>:<ul><li>• Visit our website at <strong>www.crjmc.net</strong></li><li>• Fill out the online form and verify your identity</li><li>• Receive your police record check <strong>within 15 minutes</strong></li></ul>There's <strong>no need for an in-person visit</strong>, and the process is <strong>quick, secure, and fully online</strong>."
        },
        {
          question: "How long does it take to get my police record check?",
          answer: "At CRJMC.net, you'll receive your police record check <strong>within 15 minutes</strong>. Our system is <strong>fully automated</strong> and designed to provide <strong>fast, secure results</strong>, delivered directly to your inbox. There's <strong>no need for an in-person appointment</strong> or a visit to a police station. It's convenient, quick, and fully online."
        },
        {
          question: "Do I need to visit a police station to get my record check?",
          answer: "<strong>No, you don't!</strong> At CRJMC.net, everything is done <strong>online from start to finish</strong>. There's no need to visit a police station or book an appointment. Simply complete your request online, verify your identity, and receive your check in <strong>15 minutes or less</strong>."
        },
        {
          question: "Is the process secure? How do you verify my identity?",
          answer: "<strong>Yes</strong>, the process at CRJMC.net is <strong>secure and fully compliant</strong> with Canadian privacy laws. We use <strong>ID verification technology</strong> to verify your identity online, ensuring your information remains safe and confidential. Your check results are sent <strong>directly to your email</strong>, so only you have control over who sees them."
        },
        {
          question: "What if I need to share my police record check with multiple organizations?",
          answer: "When you complete a CRJMC through CRJMC.net, you'll receive a <strong>digital copy</strong> that you can share with multiple organizations, as needed. Since you control the check, you can decide who to share it with. If you need additional copies, you can simply download them from your email."
        }
      ]
    },
    general: {
      title: "General Questions about Criminal Record and Judicial Matters Check",
      items: [
        {
          question: "What type of police record check does CRJMC.net provide?",
          answer: `At CRJMC.net, we provide the <strong>Criminal Record and Judicial Matters Check (CRJMC)</strong> for <strong>CAD $${CRJMC_PRICE}</strong>. This check is often required for employment, licensing, and volunteer opportunities, and includes information such as criminal convictions, outstanding charges, and court orders. Our process is <strong>100% online</strong>, with a <strong>15-minute turnaround time</strong>, and you don't need to visit a police station.`
        },
        {
          question: "What is included in a Criminal Record and Judicial Matters Check?",
          answer: "A <strong>CRJMC</strong> includes:<ul><li><strong>Criminal convictions</strong> (except those that have been pardoned)</li><li><strong>Outstanding charges and arrest warrants</strong></li><li><strong>Certain court orders</strong>, such as restraining orders or probation orders</li><li><strong>Absolute and conditional discharges</strong> (if applicable)</li></ul>This type of check is more detailed than a basic criminal record check and is commonly used for jobs that require a higher level of trust, such as in healthcare, finance, or legal sectors."
        },
        {
          question: "How is a Criminal Record and Judicial Matters Check different from a basic Criminal Record Check?",
          answer: "A basic Criminal Record Check only includes criminal convictions, while the <strong>Criminal Record and Judicial Matters Check (CRJMC)</strong> goes further by including:<ul><li>• <strong>Outstanding charges and arrest warrants</strong></li><li>• <strong>Certain judicial orders</strong>, such as probation orders</li><li>• <strong>Absolute and conditional discharges</strong></li></ul>The CRJMC is often required for roles that involve <strong>greater responsibility or trust</strong>."
        },
        {
          question: "Who typically needs a Criminal Record and Judicial Matters Check?",
          answer: "A <strong>CRJMC</strong> is commonly required for:<ul><li>• <strong>Employment</strong> in sectors like healthcare, finance, and education</li><li>• <strong>Professional licensing</strong></li><li>• <strong>Volunteering</strong></li><li>• <strong>Rental applications</strong></li><li>• <strong>Immigration purposes</strong></li></ul>If you've been asked to provide a background check, the CRJMC is often the right option."
        },
        {
          question: "What types of jobs require a Criminal Record and Judicial Matters Check?",
          answer: "The CRJMC is often required for jobs in industries such as:<ul><li>• <strong>Healthcare</strong></li><li>• <strong>Education</strong></li><li>• <strong>Finance</strong></li><li>• <strong>Government roles</strong></li><li>• <strong>Legal services</strong></li></ul>It's also commonly requested for <strong>volunteer positions</strong> and <strong>immigration purposes</strong>."
        },
        {
          question: "Can I use the CRJMC from CRJMC.net for volunteer positions?",
          answer: "<strong>Yes</strong>, you can use your CRJMC for most volunteer roles. However, if the organization specifically requests a <strong>Vulnerable Sector Check</strong>, you will need to obtain that directly from your local police service, as it requires fingerprinting. For most general volunteer roles, the CRJMC is sufficient."
        },
        {
          question: "Is the Criminal Record and Judicial Matters Check accepted by employers and licensing bodies?",
          answer: "<strong>Yes</strong>. The CRJMC provided by CRJMC.net is <strong>accepted across Canada</strong> by most employers, volunteer organizations, and licensing bodies. Make sure to check with the organization requesting your background check to ensure they accept the CRJMC type of record check."
        },
        {
          question: "Do I need to consent to a police record check?",
          answer: "<strong>Yes</strong>. In Ontario, <strong>consent is required</strong> before a police record check is conducted. At CRJMC.net, we ensure that your consent is obtained during the online application process. Once you receive your check results, you also have control over whether to share them with third parties."
        },
        {
          question: "What if my Criminal Record and Judicial Matters Check shows incorrect information?",
          answer: "If you believe the information in your check is incorrect, you can contact the police service that processed the check. At CRJMC.net, we'll provide <strong>guidance on how to request a correction</strong> from the appropriate authorities."
        }
      ]
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [...faqData.service.items, ...faqData.general.items].map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "CRJMC.net FAQ - Criminal Record and Judicial Matters Check",
    "description": "Find answers to common questions about getting your Criminal Record & Judicial Matters Check (CRJMC) online. Fast, secure service for background checks in Canada.",
    "url": "https://crjmc.net/faq",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://crjmc.net"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "FAQ",
          "item": "https://crjmc.net/faq"
        }
      ]
    }
  };

  return (
    <div style={{ fontFamily: "'Raleway', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif" }}>
      <SEO 
        title="CRJMC: Frequently Asked Questions | CRJMC Background Check Services"
        description="Find answers to common questions about CRJMC background check services, processing times, and requirements."
        image="/images/cleared-background-check-512.png"
        ogType="website"
        article={true}
        pathname="/faq"
      />
      <Helmet>
        <html lang="en" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://crjmc.net/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CRJMC.net" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="keywords" content="CRJMC, Criminal Record Check, Judicial Matters Check, Background Check Canada, Police Record Check, Online Background Check" />
      </Helmet>

      <Header />

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-16">
        <Breadcrumb items={[{ label: 'FAQ' }]} />

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 sm:mb-12">CRJMC: Frequently Asked Questions</h1>
        <div className="mb-8 sm:mb-16">
          <p className="text-base sm:text-lg lg:text-xl text-gray-700 mb-8 sm:mb-12 leading-relaxed">
            Find answers to common questions about our services and solutions. If you can't find what you're looking for, please don't hesitate to contact us.
          </p>

          {/* Service Questions Section */}
          <section aria-labelledby="service-questions" className="mb-8 sm:mb-16">
            <h2 id="service-questions" className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">{faqData.service.title}</h2>
            <Accordion type="single" collapsible className="w-full mb-8 sm:mb-12">
              {faqData.service.items.map((faq, index) => (
                <AccordionItem key={`service-${index}`} value={`service-${index}`}>
                  <AccordionTrigger className="text-lg sm:text-xl font-semibold py-4 sm:py-6">
                    <h3>{faq.question}</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div 
                      className="text-base sm:text-lg text-gray-700 leading-relaxed py-4 px-2 faq-content"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* General Questions Section */}
          <section aria-labelledby="general-questions">
            <h2 id="general-questions" className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">{faqData.general.title}</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqData.general.items.map((faq, index) => (
                <AccordionItem key={`general-${index}`} value={`general-${index}`}>
                  <AccordionTrigger className="text-lg sm:text-xl font-semibold py-4 sm:py-6">
                    <h3>{faq.question}</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div 
                      className="text-base sm:text-lg text-gray-700 leading-relaxed py-4 px-2 faq-content"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        <CTASection 
          title="Still have questions?"
          description="We're here to help. Get started with your CRJMC application today or contact us for more information."
        />
      </main>

      <Footer />

      {/* JSON-LD Schema */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteJsonLd)}
      </script>
    </div>
  );
};

export default FAQPage; 
